import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import {
  ADMIN_ALL_STAFFING_QUERY,
  ADMIN_ALL_CEREMONIES_QUERY,
  CREATE_STAFFING_REQUIREMENT_MUTATION,
  UPDATE_STAFFING_REQUIREMENT_MUTATION,
  DELETE_STAFFING_REQUIREMENT_MUTATION,
} from '../graphql/queries';
import { useLov } from '../hooks/useLov';
import ContractedVendorsSection from '../components/ContractedVendorsSection';
import { useFamily } from '../context/FamilyContext';
import { ownerName, actAsOwner } from '../utils/familyOwner';

const STATUS_COLOR = { pending: 'warning', confirmed: 'success', partial: 'info' };

const EMPTY = { ceremonyId: '', roleName: '', requiredCount: 1, vendorId: '', notes: '' };

export default function AdminStaffingPage() {
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [snack, setSnack]     = useState({ open: false, msg: '', ok: true });

  const { options: roleOptions } = useLov('staffing_role');

  const { family } = useFamily();
  const { data, loading, refetch } = useQuery(ADMIN_ALL_STAFFING_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: ceremonyData } = useQuery(ADMIN_ALL_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const ceremonyOptions = ceremonyData?.allCeremonies || [];

  const ceremonyLabel = (c) => {
    const owner = c.user?.profile?.firstName
      ? `${c.user.profile.firstName} ${c.user.profile.lastName || ''}`.trim()
      : c.user?.email;
    return `${c.name || 'Unknown ceremony'}${owner ? ` — ${owner}` : ''}`;
  };

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [create, { loading: creating }] = useMutation(CREATE_STAFFING_REQUIREMENT_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Staffing requirement created.'); },
    onError: e => toast(e.message, false),
  });
  const [update, { loading: updating }] = useMutation(UPDATE_STAFFING_REQUIREMENT_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Updated.'); },
    onError: e => toast(e.message, false),
  });
  const [remove] = useMutation(DELETE_STAFFING_REQUIREMENT_MUTATION, {
    onCompleted: () => { refetch(); toast('Deleted.'); },
    onError: e => toast(e.message, false),
  });

  const requirements = data?.allStaffingRequirements || [];

  const openEdit = (r) => {
    setEditing(r);
    setForm({ ceremonyId: r.ceremonyId || '', roleName: r.roleName, requiredCount: r.requiredCount, vendorId: r.vendorId || '', notes: r.notes || '' });
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY); };

  const handleSave = () => {
    if (!form.roleName.trim()) return;
    const vars = { ...form, requiredCount: parseInt(form.requiredCount) || 1 };
    if (editing) {
      update({ variables: { id: editing.id, roleName: vars.roleName, requiredCount: vars.requiredCount, notes: vars.notes }, ...actAsOwner(editing.owner) });
    } else {
      if (!vars.ceremonyId) return toast('Select a ceremony first.', false);
      create({ variables: { ceremonyId: vars.ceremonyId, roleName: vars.roleName, requiredCount: vars.requiredCount, notes: vars.notes } });
    }
  };

  const summaryByRole = requirements.reduce((acc, r) => {
    acc[r.roleName] = (acc[r.roleName] || 0) + r.requiredCount;
    return acc;
  }, {});

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Staffing Requirements</Typography>
          <Typography variant="body2" color="text.secondary">
            {family ? `${family.displayName}'s staffing requirements` : 'Staffing requirements of all bride/groom families'}
          </Typography>
        </Box>
        <Tooltip title={family ? '' : 'Select a bride/groom family above to add a requirement for them'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!family} onClick={() => { setEditing(null); setForm(EMPTY); setDialog(true); }}>Add Requirement</Button>
          </span>
        </Tooltip>
      </Stack>

      <ContractedVendorsSection category="staffing" />

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      {/* Summary Chips */}
      {Object.keys(summaryByRole).length > 0 && (
        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
          {Object.entries(summaryByRole).map(([role, count]) => (
            <Chip key={role} label={`${role}: ${count}`} size="small" variant="outlined" />
          ))}
        </Stack>
      )}

      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {!family && <TableCell>Family</TableCell>}
              <TableCell>Ceremony</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Confirmed</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requirements.map(r => (
              <TableRow key={r.id} hover>
                {!family && <TableCell>{ownerName(r.owner)}</TableCell>}
                <TableCell>{r.ceremony?.name || '—'}</TableCell>
                <TableCell><strong>{r.roleName}</strong></TableCell>
                <TableCell>{r.requiredCount}</TableCell>
                <TableCell>{r.confirmedCount}</TableCell>
                <TableCell>{r.vendor?.name || '—'}</TableCell>
                <TableCell><Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} /></TableCell>
                <TableCell>{r.notes || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: r.id }, ...actAsOwner(r.owner) })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {requirements.length === 0 && (
              <TableRow><TableCell colSpan={family ? 8 : 9} align="center">{family ? `No staffing requirements for ${family.displayName} yet.` : 'No staffing requirements found.'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit Staffing Requirement' : 'New Staffing Requirement'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {editing ? (
              <TextField label="Ceremony" value={ceremonyLabel(editing.ceremony || {})} disabled fullWidth
                helperText="The ceremony can't be changed after creation." />
            ) : (
              <TextField select label="Ceremony" value={form.ceremonyId} onChange={e => setForm(f => ({ ...f, ceremonyId: e.target.value }))} required fullWidth>
                {ceremonyOptions.length === 0 && <MenuItem value="" disabled><em>{family ? `${family.displayName} has no ceremonies yet — add one under Ritual Planning` : 'No ceremonies found'}</em></MenuItem>}
                {ceremonyOptions.map(c => <MenuItem key={c.id} value={c.id}>{ceremonyLabel(c)}</MenuItem>)}
              </TextField>
            )}
            <TextField select label="Role" value={form.roleName} onChange={e => setForm(f => ({ ...f, roleName: e.target.value }))} required fullWidth>
              {roleOptions.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
            <TextField label="Required Count" value={form.requiredCount} onChange={e => setForm(f => ({ ...f, requiredCount: e.target.value }))} type="number" fullWidth inputProps={{ min: 1 }} />
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} startIcon={<SaveIcon />}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
